import React, { useEffect, useState } from "react";
import { FileDown, Loader2, PhoneCall, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Dialog } from "../inbox/InboxDialogs";

const openBlob = async (url) => {
  const r = await api.get(url, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
};

function YousignDialog({ sav, onClose, onSent }) {
  const c = sav.client || {};
  const [f, setF] = useState({ first_name: c.prenom || "", last_name: c.nom || "", email: c.email || "", phone: c.telephone || "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/sav/${sav.id}/attestation/yousign`, f); toast.success("Attestation envoyée à signer (Yousign)"); onSent(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Faire signer l'attestation par Yousign" onClose={onClose} testid="sav-yousign-dialog"
      footer={<><button onClick={onClose} className="btn-ghost text-sm">Annuler</button>
        <button onClick={send} disabled={busy} className="btn-primary text-sm" data-testid="sav-yousign-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer</button></>}>
      <div className="grid grid-cols-2 gap-2">
        {[["first_name", "Prénom"], ["last_name", "Nom"], ["email", "Email"], ["phone", "Téléphone (code SMS, facultatif)"]].map(([k, l]) => (
          <label key={k} className="block space-y-1 text-[11px] text-norea-textMuted">{l}
            <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-field text-sm w-full" data-testid={`sav-yousign-${k}`} /></label>
        ))}
      </div>
      <div className="text-[11px] text-norea-textMuted">Une fois signée, l'attestation est rangée automatiquement dans les pièces de clôture.</div>
    </Dialog>
  );
}

export function AttestationActions({ sav, state, reload }) {
  const [dlg, setDlg] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => openBlob(`/sav/${sav.id}/attestation.pdf`).catch(() => toast.error("Génération impossible"))} className="btn-secondary text-xs" data-testid="sav-attestation-pdf">
          <FileDown className="w-3.5 h-3.5" /> Attestation pré-remplie (à imprimer)</button>
        <button onClick={() => setDlg(true)} disabled={!state?.yousign} title={state?.yousign ? "" : "Yousign pas encore connecté : clé à saisir dans Configuration → Connexions"}
          className="btn-secondary text-xs" data-testid="sav-attestation-yousign"><Send className="w-3.5 h-3.5" /> Faire signer par Yousign</button>
      </div>
      {!state?.yousign && <div className="text-[11px] text-norea-textMuted" data-testid="sav-yousign-off">Yousign pas encore connecté : faites signer sur papier puis déposez la photo ou le scan.</div>}
      {(state?.signatures || []).map((s) => (
        <div key={s.id} className="text-[11px] text-norea-textMuted" data-testid={`sav-signature-${s.id}`}>Yousign · {s.signer?.email} · <strong>{s.status_label}</strong></div>
      ))}
      {dlg && <YousignDialog sav={sav} onClose={() => setDlg(false)} onSent={reload} />}
    </div>
  );
}

export function RingoverCalls({ sav, state, onAttached }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (state?.ringover) api.get(`/sav/${sav.id}/ringover/calls`).then((r) => setData(r.data)).catch((e) => setData({ error: e?.response?.data?.detail || "Ringover indisponible", calls: [] }));
  }, [sav.id, state?.ringover]);
  if (!state) return null;
  if (!state.ringover) return <div className="text-[11px] text-norea-textMuted" data-testid="sav-ringover-off">Ringover pas encore connecté (clé à saisir dans Configuration → Connexions) : déposez le vocal manuellement.</div>;
  const attach = async (c) => {
    setBusy(c.call_id);
    try { await api.post(`/sav/${sav.id}/ringover/${c.call_id}/attach`); toast.success("Appel rattaché comme vocal de clôture"); onAttached(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rattachement impossible"); }
    finally { setBusy(null); }
  };
  return (
    <div className="rounded border border-norea-border bg-norea-bg/40 p-2 space-y-1" data-testid="sav-ringover-calls">
      <div className="text-[11px] font-medium inline-flex items-center gap-1"><PhoneCall className="w-3 h-3" />Appels Ringover avec le client depuis l'ouverture du SAV</div>
      {!data ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : data.error ? <div className="text-[11px] text-amber-800">{data.error}</div>
        : data.calls.length === 0 ? <div className="text-[11px] italic text-norea-textMuted" data-testid="sav-ringover-empty">Aucun appel enregistré avec ce numéro depuis l'ouverture.</div>
          : data.calls.map((c) => (
            <div key={c.call_id} className="flex items-start gap-2 text-[11px]" data-testid={`sav-call-${c.call_id}`}>
              <span className="w-28 shrink-0">{new Date(c.start).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
              <span className="flex-1 text-norea-textMuted line-clamp-2">{c.summary || c.transcript || (c.has_record ? "Enregistrement disponible (transcrit au rattachement)" : "Pas d'enregistrement")}</span>
              {c.attached ? <span className="text-emerald-700">rattaché</span> : c.has_record && (
                <button onClick={() => attach(c)} disabled={busy === c.call_id} className="btn-ghost text-[11px]" data-testid={`sav-call-attach-${c.call_id}`}>
                  {busy === c.call_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Rattacher comme vocal de clôture"}</button>)}
            </div>
          ))}
    </div>
  );
}
